import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Download, Loader2, RefreshCw, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const ETAPAS = { crm: 'CRM', fechas: 'Fechas', correo: 'Correo', arquitecto: 'Arquitecto' };

export const ErroresImportacion = ({ jobId, errores, total }) => {
  const [descargando, setDescargando] = useState(false);
  const descargar = async () => {
    setDescargando(true);
    try {
      const resp = await api.get(`/entregas/ingest/jobs/${jobId}/errores.xlsx`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `errores_importacion_${jobId.slice(0, 8)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar el archivo de errores');
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="border border-red-200 rounded-lg overflow-hidden" data-testid="import-errores">
      <div className="bg-red-50 px-3 py-2 text-xs font-medium text-red-700 flex items-center justify-between gap-2">
        <span>Errores ({total ?? errores.length}){total > errores.length ? ` — se muestran ${errores.length}` : ''}</span>
        {jobId && (
          <Button size="sm" variant="outline" onClick={descargar} disabled={descargando}
            className="h-6 text-[11px] border-red-300 text-red-700" data-testid="btn-descargar-errores">
            {descargando ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Download size={12} className="mr-1" />}
            Descargar errores (Excel)
          </Button>
        )}
      </div>
      <div className="max-h-[240px] overflow-y-auto">
        <table className="w-full text-xs" data-testid="tabla-errores-importacion">
          <thead>
            <tr className="border-b bg-[#FAFAFA]">
              <th className="text-left p-2 font-medium w-14">Fila</th>
              <th className="text-left p-2 font-medium w-24">Columna</th>
              <th className="text-left p-2 font-medium">Error</th>
            </tr>
          </thead>
          <tbody>
            {errores.map((err, i) => (
              <tr key={i} className="border-b border-[#F0F0F0]" data-testid={`error-importacion-${i}`}>
                <td className="p-2 font-mono text-red-600">{err.fila ?? '—'}</td>
                <td className="p-2 text-[#5A5A5A]">{err.columna || '—'}</td>
                <td className="p-2 text-[#5A5A5A]">{err.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AdvertenciasImportacion = ({ jobId, advertencias, onReintentado }) => {
  const [enviando, setEnviando] = useState(false);
  const hayCrm = advertencias.some(a => a.etapa === 'crm');
  const reintentar = async () => {
    setEnviando(true);
    try {
      const resp = await api.post(`/entregas/ingest/jobs/${jobId}/reintentar-crm`);
      toast.success(`Reintento de consulta CRM iniciado para ${resp.data.total} entrega(s)`);
      onReintentado?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo reintentar la consulta CRM');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="border border-amber-200 rounded-lg overflow-hidden" data-testid="import-advertencias">
      <div className="bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1"><AlertTriangle size={12} /> Advertencias ({advertencias.length})</span>
        {hayCrm && (
          <Button size="sm" variant="outline" onClick={reintentar} disabled={enviando}
            className="h-6 text-[11px] border-amber-300 text-amber-800" data-testid="btn-reintentar-crm">
            {enviando ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCw size={12} className="mr-1" />}
            Reintentar consulta CRM
          </Button>
        )}
      </div>
      <div className="max-h-[200px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-[#FAFAFA]">
              <th className="text-left p-2 font-medium">Entrega</th>
              <th className="text-left p-2 font-medium">Etapa</th>
              <th className="text-left p-2 font-medium">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {advertencias.map((a, i) => (
              <tr key={i} className="border-b border-[#F0F0F0]" data-testid={`advertencia-importacion-${i}`}>
                <td className="p-2 font-mono text-[#5A5A5A] whitespace-nowrap">{a.codigo || '—'}{a.proforma ? ` · ${a.proforma}` : ''}</td>
                <td className="p-2 text-[#5A5A5A]">{ETAPAS[a.etapa] || a.etapa}</td>
                <td className="p-2 text-[#5A5A5A] [overflow-wrap:anywhere]">{a.mensaje}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const ImportacionCancelada = ({ mensaje }) => (
  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="import-cancelado">
    <XCircle size={16} className="mt-0.5 flex-shrink-0" />
    <span>{mensaje}</span>
  </div>
);
